import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BRIGHTNESS_PRESETS,
  buildCoefficientList,
  formatBrightnessSummary,
  isContrastSelectionValid,
} from "./installationBrightnessUtils";
import InstallationBrightnessSegment from "./InstallationBrightnessSegment";

const InstallationBrightnessContrastControls = ({
  shadowLevel,
  highlightLevel,
  onShadowLevelChange,
  onHighlightLevelChange,
  disabled,
}) => {
  const { t } = useTranslation();

  const shadowLevels = useMemo(
    () => [
      { value: 0, label: t("installation_brightness_shadows_none") },
      { value: 1, label: t("installation_brightness_shadows_light") },
      { value: 2, label: t("installation_brightness_shadows_medium") },
      { value: 3, label: t("installation_brightness_shadows_strong") },
    ],
    [t],
  );

  const highlightLevels = useMemo(
    () => [
      { value: 0, label: t("installation_brightness_highlights_none") },
      { value: 1, label: t("installation_brightness_highlights_light") },
      { value: 2, label: t("installation_brightness_highlights_medium") },
      { value: 3, label: t("installation_brightness_highlights_strong") },
    ],
    [t],
  );

  const coefficients = useMemo(
    () => buildCoefficientList(shadowLevel, highlightLevel),
    [shadowLevel, highlightLevel],
  );

  const summary = formatBrightnessSummary(t, {
    mode: "contrast",
    shadowLevel,
    highlightLevel,
    coefficients,
  });

  const selectionValid = isContrastSelectionValid(shadowLevel, highlightLevel);

  const presets = useMemo(
    () => [
      {
        id: "balanced",
        label: t("installation_brightness_preset_balanced"),
        ...BRIGHTNESS_PRESETS.balanced,
      },
      {
        id: "shadowPriority",
        label: t("installation_brightness_preset_shadow_priority"),
        ...BRIGHTNESS_PRESETS.shadowPriority,
      },
      {
        id: "highlightPriority",
        label: t("installation_brightness_preset_highlight_priority"),
        ...BRIGHTNESS_PRESETS.highlightPriority,
      },
      {
        id: "wide",
        label: t("installation_brightness_preset_wide"),
        ...BRIGHTNESS_PRESETS.wide,
      },
    ],
    [t],
  );

  return (
    <div className="installation-brightness-contrast">
      <p className="installation-brightness-summary">{summary}</p>

      {!selectionValid && (
        <p className="installation-brightness-validation" role="status">
          {t("installation_brightness_contrast_validation")}
        </p>
      )}

      <InstallationBrightnessSegment
        label={t("installation_brightness_shadows_title")}
        levels={shadowLevels}
        value={shadowLevel}
        onChange={onShadowLevelChange}
        disabled={disabled}
      />

      <p className="installation-brightness-reference">
        {t("installation_brightness_reference")}
      </p>

      <InstallationBrightnessSegment
        label={t("installation_brightness_highlights_title")}
        levels={highlightLevels}
        value={highlightLevel}
        onChange={onHighlightLevelChange}
        disabled={disabled}
      />

      <div className="installation-brightness-presets">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className="installation-brightness-preset-chip"
            onClick={() => {
              onShadowLevelChange(preset.shadowLevel);
              onHighlightLevelChange(preset.highlightLevel);
            }}
            disabled={disabled}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default InstallationBrightnessContrastControls;
